import {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import moment from 'moment';
import {Box, Skeleton, Stack, SxProps, Typography} from '@mui/material';
import {useHistory, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {ContentPage} from '../../../../components/content-page/content-page';
import DateCarousel from '../../../../components/daily-care-log/date-carousel/date-carousel';
import {DailyCareLogContext} from '../../../../contexts/daily-care-log/context';
import {EntityShiftsRepository} from '../../../../repositories/entity-shifts';
import {translations} from '../../../../i18n/translations';
import {
  ShiftCard,
  ShiftAllocation,
} from '../../../../components/daily-care-log/shift-card/shift-card';
import {EntityShiftAllocation} from '../../../../backend/interfaces/entity-shift-allocation';
import {useAuth} from '../../../../contexts/auth/context';
import {EntityShiftsAllocationsRepository} from '../../../../repositories/entity-shifts-allocations';
import {ScreenPaths} from '../../../../enums';
import {useFetchAllItems, useFetchCompleteList} from '../../../../hooks/fetch-all-items';
import {useSearchParams} from '../../../../hooks/use-search-params';
import {EntityCareServiceRepository} from '../../../../repositories/entity-care-services';
import {CareServiceCard} from '../../../../components/daily-care-log/shift-card/care-service-card';
import {AffiliatedUser} from '../../../../backend/responses/affiliated-user';
import './home.daily-care-log.screen.scss';
import {RolePermissionType} from '../../../../backend/enums/role-permission-type';

const sortShiftAllocations = (shiftAllocations: ShiftAllocation[]) => {
  return shiftAllocations.sort((shiftAllocationA, shiftAllocationB) => {
    const sortByDay = moment(shiftAllocationA.allocation.day).diff(
      moment(shiftAllocationB.allocation.day)
    );

    if (sortByDay !== 0) return sortByDay;

    // extract time-only components for comparison (ignore date part)
    const startTimeA =
      moment(shiftAllocationA.shift.startHour).hours() * 60 +
      moment(shiftAllocationA.shift.startHour).minutes();
    const startTimeB =
      moment(shiftAllocationB.shift.startHour).hours() * 60 +
      moment(shiftAllocationB.shift.startHour).minutes();

    // sort by start time first
    if (startTimeA !== startTimeB) return startTimeA - startTimeB;
    // if start times are equal, sort by end time
    const endTimeA =
      moment(shiftAllocationA.shift.endHour).hours() * 60 +
      moment(shiftAllocationA.shift.endHour).minutes();
    const endTimeB =
      moment(shiftAllocationB.shift.endHour).hours() * 60 +
      moment(shiftAllocationB.shift.endHour).minutes();

    return endTimeA - endTimeB;
  });
};

export const SkeletonsList = ({count}: {count: number}) => {
  return (
    <Stack gap={2} flexDirection="column" width="100%">
      {new Array(count).fill(1).map((_item, index) => (
        <Skeleton key={`shift_card_skeleton_${index}`} variant="rounded" width="100%" height={56} />
      ))}
    </Stack>
  );
};

const noShiftsTextSx: SxProps = {
  fontSize: '16px',
  fontWeight: 500,
  lineHeight: '20px', // Match the ShiftCard height so that the transition between loading and the text is smooth
  color: 'var(--dcm-gray)',
};

export default function HomeDailyCareLogScreen({seniors}: {seniors?: AffiliatedUser[]}) {
  const [shiftsAllocations, setShiftsAllocations] = useState<EntityShiftAllocation[] | undefined>(
    undefined
  );
  const {entityId}: {entityId: string} = useParams();
  const {t} = useTranslation();
  const dailyCareLogContext = useContext(DailyCareLogContext);
  const {hasReadPermission, state: authState} = useAuth();
  const canReadCareServices = useMemo(
    () =>
      hasReadPermission(RolePermissionType.Team_CareServices) ||
      hasReadPermission(RolePermissionType.Seniors_CareServices) ||
      hasReadPermission(RolePermissionType.Team_Professionals_CareServices),
    [hasReadPermission]
  );
  const caregiverId = authState.session?.id;
  let history = useHistory();
  const searchParams = useSearchParams();
  const date = searchParams.get('date');
  const selectedDay = useMemo(() => {
    const d = moment(date);

    return d.isValid() ? d : moment();
  }, [date]);

  const {items: shifts} = useFetchAllItems({
    fetchFunction: ({limit, skip}) => {
      return EntityShiftsRepository.instance.getShifts(entityId, limit, skip);
    },
  });

  const careServicesData = useFetchCompleteList({
    fetchFunction: () => {
      return EntityCareServiceRepository.instance.getItems(
        entityId,
        caregiverId!,
        selectedDay.clone().startOf('day'),
        selectedDay.clone().endOf('day'),
        'caregiver'
      );
    },
    transform: (data) => {
      return data.sort((a, b) => {
        const startDiff = moment(a.startsAt).diff(moment(b.startsAt));
        // sort by start time first
        if (startDiff !== 0) return startDiff;
        // if start times are equal, sort by end time
        return moment(a.endsAt).diff(moment(b.endsAt));
      });
    },
    deps: [entityId, caregiverId, selectedDay],
  });

  const defaultShiftAllocations: EntityShiftAllocation[] = useMemo(() => {
    if (!shifts) return [];

    const dayStr = moment(selectedDay).format('YYYY-MM-DD');

    return shifts.map((shift, index) => {
      return {
        id: '',
        createdAt: '',
        updatedAt: '',
        entityId: entityId,
        userId: shift.responsibleUserId || '',
        day: dayStr,
        entityShiftId: shift.id || index.toString(),
      };
    });
  }, [entityId, selectedDay, shifts]);

  const caregiverShiftAllocations: ShiftAllocation[] | undefined = useMemo(() => {
    if (!shifts || !shiftsAllocations) return undefined;

    // Get all caregiver allocations
    const caregiverAllocations = shiftsAllocations.filter(
      (allocation) => allocation.userId === caregiverId
    );

    // Map the allocations from the caregiver along with the respective shift
    const filteredShiftAllocations = caregiverAllocations.map((allocation) => {
      return {
        shift: shifts.find((shift) => allocation.entityShiftId === shift.id)!,
        allocation: allocation,
        isDayBefore: !moment(allocation.day).isSame(selectedDay, 'day'),
      };
    });

    // Return the filtered shifts, sorted by their start time field
    return sortShiftAllocations(filteredShiftAllocations);
  }, [shifts, shiftsAllocations, caregiverId, selectedDay]);

  const otherShiftAllocations: ShiftAllocation[] | undefined = useMemo(() => {
    if (!shifts || !shiftsAllocations || !caregiverShiftAllocations) return undefined;

    // Get allocations for all other caregivers
    let otherAllocations = shiftsAllocations.filter(
      (allocation) => allocation.userId !== caregiverId
    );
    if (otherAllocations.length === 0) {
      if (caregiverShiftAllocations.length === 0) {
        otherAllocations = defaultShiftAllocations;
      } else {
        otherAllocations = defaultShiftAllocations.filter(
          (allocation) =>
            !caregiverShiftAllocations.some(
              (shiftAllocation) => shiftAllocation.shift.id === allocation.entityShiftId
            )
        );
      }
    } else {
      otherAllocations = otherAllocations.concat(
        defaultShiftAllocations.filter(
          (allocation) =>
            !caregiverShiftAllocations.some(
              (shiftAllocation) => shiftAllocation.shift.id === allocation.entityShiftId
            )
        )
      );
    }

    const filteredShiftAllocations = otherAllocations.reduce(
      (result: ShiftAllocation[], allocation) => {
        if (
          !result.find(
            (shiftAllocation) => shiftAllocation.shift.id === allocation.entityShiftId
          ) &&
          !caregiverShiftAllocations.some(
            (shiftAllocation) =>
              shiftAllocation.shift.id === allocation.entityShiftId &&
              shiftAllocation.allocation.day === allocation.day
          )
        ) {
          result.push({
            shift: shifts.find((shift) => allocation.entityShiftId === shift.id)!,
            allocation,
            isDayBefore: !moment(allocation.day).isSame(selectedDay, 'day'),
          });
        }

        return result;
      },
      []
    );

    // Return the filtered shifts, sorted by their start time field
    return sortShiftAllocations(filteredShiftAllocations);
  }, [
    shifts,
    shiftsAllocations,
    caregiverShiftAllocations,
    caregiverId,
    defaultShiftAllocations,
    selectedDay,
  ]);

  const fetchShiftsAllocations = useCallback(async () => {
    // Reset shift allocations when re-fetching for a different day so that skeletons are rendered
    setShiftsAllocations(undefined);

    const response =
      await EntityShiftsAllocationsRepository.instance.getListFormatEntityShiftsAllocations(
        entityId,
        selectedDay.clone().format('YYYY-MM-DD'),
        selectedDay.clone().add(1, 'day').format('YYYY-MM-DD')
      );

    setShiftsAllocations(response);
  }, [entityId, selectedDay]);

  const redirectToShiftDetail = (shiftAllocation: ShiftAllocation, careServiceUserId?: string) => {
    const {shift, allocation} = shiftAllocation;
    const shiftStartHour = moment(shift.startHour);
    let shiftEndHour = moment(shift.endHour);
    const date = moment(allocation.day).format('YYYY-MM-DD');

    // Daily Care Log Limitation: Daily Care log does not support +24h shifts.
    if (Math.abs(shiftEndHour.diff(shiftStartHour, 'hours')) >= 24) {
      shiftEndHour = shiftStartHour.clone().add(23, 'hours').add(59, 'minutes');
    } else if (shiftStartHour.isSame(shiftEndHour)) {
      shiftEndHour.add(23, 'hours').add(59, 'minutes');
    }

    let sufix = '';
    if (careServiceUserId) {
      sufix = `&careServiceUserId=${careServiceUserId}`;
    }

    history.push(
      `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_Shift}/${shift?.id}?date=${date}&start=${shiftStartHour.format('HH:mm')}&end=${shiftEndHour.format('HH:mm')}${sufix}`,
      {
        shift,
      }
    );
  };

  useEffect(() => {
    if (entityId === undefined) return;
    fetchShiftsAllocations();
  }, [entityId, fetchShiftsAllocations]);

  useEffect(() => {
    dailyCareLogContext.setNavigationHeader(false);
    dailyCareLogContext.toggleBottomNavigation(true, true);

    return () => {
      dailyCareLogContext.setNavigationHeader(false);
      dailyCareLogContext.toggleBottomNavigation(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ContentPage>
      <DateCarousel
        selectedDate={selectedDay}
        onDateChanged={(newDate) => {
          // Update URL
          searchParams.set('date', newDate.format('YYYY-MM-DD'));
          history.push({
            search: searchParams.toString(),
          });
        }}
      />

      <Box className="home-shifts">
        <Box className="shifts-section">
          <Typography>{t(translations.daily_care_log.home.care_services)}</Typography>

          <Stack direction="column" spacing={1.5}>
            {!canReadCareServices && (
              <Typography sx={noShiftsTextSx}>
                {t(translations.care_services.permissions.view)}
              </Typography>
            )}
            {careServicesData.isLoading ? (
              <SkeletonsList count={3} />
            ) : careServicesData.items?.length === 0 ? (
              <Typography sx={noShiftsTextSx}>
                {t(translations.daily_care_log.home.no_care_services)}
              </Typography>
            ) : (
              careServicesData.items?.map((item) => {
                const senior = seniors?.find((s) => s.user.id! === item.userId);
                const seniorName =
                  senior?.user.displayName || senior?.user.name || senior?.user.id || '';
                let label = seniorName;

                if (item.title) {
                  label += ' | ' + item.title;
                }

                return (
                  <CareServiceCard
                    key={item.id}
                    label={label}
                    startHour={item.startsAt}
                    endHour={item.endsAt}
                    borderColor={item.foregroundColor}
                    onClick={() => {
                      // --- Care Service HACK ---
                      // tranform CareService into ShiftAllocation to reuse the same redirect(aka same pages)
                      let hackItem: ShiftAllocation = {
                        shift: {
                          id: item.id,
                          name: item.title || seniorName || '',
                          abbreviation: '',
                          startHour: moment(item.startsAt).toDate(),
                          endHour: moment(item.endsAt).toDate(),
                          backgroundColor: item.foregroundColor ?? '#fff',
                          entityId: entityId,
                          responsibleUserId: undefined,
                          foregroundColor: '#FFF',
                          // @ts-ignore
                          userId: item.userId,
                        },
                        allocation: {
                          id: item.id,
                          createdAt: '',
                          updatedAt: '',
                          entityId: entityId,
                          day: item.startsAt,
                          entityShiftId: item.id,
                          userId: item.userId,
                        },
                        isDayBefore: !moment(item.startsAt).isSame(selectedDay, 'day'),
                      };

                      redirectToShiftDetail(hackItem, item.userId);
                    }}
                  />
                );
              })
            )}
          </Stack>
        </Box>
        <Box className="shifts-section">
          <Typography>{t(translations.daily_care_log.home.my_shifts)}</Typography>
          <Stack direction="column" spacing={1.5}>
            {caregiverShiftAllocations === undefined ? (
              <SkeletonsList count={1} />
            ) : caregiverShiftAllocations.length === 0 ? (
              <Typography sx={noShiftsTextSx}>
                {t(translations.daily_care_log.home.no_allocated_shifts)}
              </Typography>
            ) : (
              caregiverShiftAllocations.map((shiftAllocation: ShiftAllocation) => (
                <ShiftCard
                  key={`${shiftAllocation.shift.id}_${shiftAllocation.allocation.id}`}
                  shiftAllocation={shiftAllocation}
                  onClick={() => redirectToShiftDetail(shiftAllocation)}
                />
              ))
            )}
          </Stack>
        </Box>
        <Box className="shifts-section">
          <Typography>{t(translations.daily_care_log.home.other_shifts)}</Typography>
          <Stack direction="column" spacing={1.5}>
            {otherShiftAllocations === undefined ? (
              <SkeletonsList count={3} />
            ) : otherShiftAllocations.length === 0 ? (
              <Typography sx={noShiftsTextSx}>
                {t(translations.daily_care_log.home.no_other_shifts)}
              </Typography>
            ) : (
              otherShiftAllocations.map((shiftAllocation: ShiftAllocation) => (
                <ShiftCard
                  key={`${shiftAllocation.shift.id}_${shiftAllocation.allocation.id}`}
                  shiftAllocation={shiftAllocation}
                  onClick={() => redirectToShiftDetail(shiftAllocation)}
                />
              ))
            )}
          </Stack>
        </Box>
      </Box>
    </ContentPage>
  );
}
